// gtCsv — reads a CSV file in the browser. Used by every import on the platform.
//
// Handles what real exports throw at it: quoted fields with commas and line
// breaks inside, doubled quotes, Windows line endings, a byte-order mark at the
// start, and files separated by semicolons or tabs instead of commas.
// Returns { headers: [...], rows: [[...], ...] } with blank lines dropped.

function detectDelimiter(text) {
  const firstLine = text.split(/\r?\n/, 1)[0] || '';
  const counts = { ',': 0, ';': 0, '\t': 0 };
  let inQuotes = false;
  for (const ch of firstLine) {
    if (ch === '"') inQuotes = !inQuotes;
    else if (!inQuotes && counts[ch] !== undefined) counts[ch] += 1;
  }
  return Object.keys(counts).sort((a, b) => counts[b] - counts[a])[0] || ',';
}

export function parseCsv(input) {
  let text = String(input || '');
  if (text.charCodeAt(0) === 0xfeff) text = text.slice(1);
  const delim = detectDelimiter(text);
  const rows = [];
  let row = [];
  let field = '';
  let inQuotes = false;
  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') { field += '"'; i += 1; } else inQuotes = false;
      } else field += ch;
      continue;
    }
    if (ch === '"') { inQuotes = true; continue; }
    if (ch === delim) { row.push(field); field = ''; continue; }
    if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && text[i + 1] === '\n') i += 1;
      row.push(field); field = '';
      rows.push(row); row = [];
      continue;
    }
    field += ch;
  }
  if (field !== '' || row.length) { row.push(field); rows.push(row); }
  const nonBlank = rows.filter(r => r.some(c => String(c).trim() !== ''));
  if (!nonBlank.length) return { headers: [], rows: [] };
  const headers = nonBlank[0].map((h, i) => String(h).trim() || 'Column ' + (i + 1));
  return { headers, rows: nonBlank.slice(1) };
}

// Match each column heading to a field, using the field's aliases. Several
// columns may feed a field that joins them (City, State, Country into Location).
export function autoMap(headers, fields) {
  const mapping = {};
  const taken = {};
  const norm = s => String(s).toLowerCase().replace(/[_\-.]+/g, ' ').replace(/\s+/g, ' ').trim();
  headers.forEach((h, i) => {
    const n = norm(h);
    const f = fields.find(x => (x.aliases || []).indexOf(n) !== -1 || norm(x.label) === n || x.key === n);
    if (!f) return;
    if (taken[f.key] && !f.joiner) return;
    mapping[i] = f.key;
    taken[f.key] = true;
  });
  return mapping;
}

// Turn the file's rows into { field: value } using the mapping.
export function applyMapping(rows, mapping, fields) {
  const joinerOf = {};
  fields.forEach(f => { joinerOf[f.key] = f.joiner || ' '; });
  const cols = Object.keys(mapping).map(Number).sort((a, b) => a - b);
  return rows.map(r => {
    const out = {};
    cols.forEach(i => {
      const key = mapping[i];
      const v = String(r[i] == null ? '' : r[i]).trim();
      if (!key || !v) return;
      out[key] = out[key] ? out[key] + joinerOf[key] + v : v;
    });
    return out;
  });
}
